import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { Doc, DocId } from 'records/Doc';
import { ProjectId } from 'records/Project';
import {
  deleteDocument,
  getDocument,
  getDocuments,
  uploadDocument,
  uploadDocuments,
} from 'services/documentService';

interface DocumentServiceToastText {
  drawingsUploaded: {
    title: string;
    message: string;
  };
}

export interface DocumentActions {
  getProjectDocuments: (
    actionsParams: AppStateActionParams,
    projectId: ProjectId
  ) => Promise<AppState>;

  getDocument: (
    actionParams: AppStateActionParams,
    projectId: ProjectId,
    docId: DocId
  ) => Promise<AppState>;

  getDocuments: (
    actionParams: AppStateActionParams,
    projectId: ProjectId,
    docIds: Immutable.List<DocId>
  ) => Promise<AppState>;

  uploadDocument: (
    actionParams: AppStateActionParams,
    projectId: string,
    document: File,
    type?: string
  ) => Promise<AppState>;

  deleteDocument: (
    actionParams: AppStateActionParams,
    projectId: string,
    documentId: string
  ) => Promise<AppState>;

  uploadDocuments: (
    actionParams: AppStateActionParams,
    projectId: string,
    documents: File[],
    toastText: DocumentServiceToastText
  ) => Promise<AppState>;

  clearDocuments: (actionParams: AppStateActionParams) => AppState;
}

const documentActions: DocumentActions = {
  getProjectDocuments: async (
    { state }: AppStateActionParams,
    projectId: ProjectId
  ): Promise<AppState> => {
    const fetchedDocs = await getDocuments(projectId);

    return state().update('documents', (docs) => {
      return fetchedDocs.reduce((acc, d) => {
        return acc.set(d.documentid, d);
      }, docs);
    });
  },

  getDocument: async (
    { state }: AppStateActionParams,
    projectId: ProjectId,
    docId: DocId
  ): Promise<AppState> => {
    const doc = await getDocument(projectId, docId);

    return state().setIn(['documents', doc.documentid], doc);
  },

  getDocuments: async (
    { state }: AppStateActionParams,
    projectId: ProjectId,
    docIds: Immutable.List<DocId>
  ): Promise<AppState> => {
    const fetchedDocs = await Promise.all(
      docIds.map((id) => getDocument(projectId, id))
    );

    return state().update('documents', (docs) => {
      return fetchedDocs.reduce((acc, d) => {
        return acc.set(d.documentid, d);
      }, docs);
    });
  },

  uploadDocument: async (
    { state, actions }: AppStateActionParams,
    projectId: string,
    document: File,
    type?: string
  ): Promise<AppState> => {
    // TODO: Not sure how to store documents yet, so just uploading for now.
    await uploadDocument(projectId, document, type);

    // make sure to get the newly created processingStatuses
    window.setTimeout(actions.getProcessingStatuses, 400);

    return state();
  },

  deleteDocument: async (
    { state }: AppStateActionParams,
    projectId: string,
    documentId: string
  ): Promise<AppState> => {
    await deleteDocument(projectId, documentId);
    return state();
  },

  uploadDocuments: async (
    { state, actions }: AppStateActionParams,
    projectId: string,
    documents: File[],
    toastText: DocumentServiceToastText
  ): Promise<AppState> => {
    // TODO: Not sure how to store documents yet, so just uploading for now.
    await uploadDocuments(
      projectId,
      documents,
      toastText,
      (progress) => {
        actions.updateActiveUploads(progress);
      },
      (documentId) => {
        if (state().uploadCancelationRequests.includes(documentId)) {
          actions.clearProcessingStatuses();
          return true;
        }
        return false;
      }
    );
    // remove any in uploadsToCancel

    // make sure to get the newly created processingStatuses
    window.setTimeout(actions.getProcessingStatuses, 400);

    return state();
  },

  clearDocuments: ({ state }: AppStateActionParams): AppState => {
    return state().set('documents', Immutable.OrderedMap<DocId, Doc>());
  },
};

export default documentActions;
